import type { UserRole } from './SessionProvider';

/**
 * Canonical list of nav-visible top-level modules. The Supabase
 * `menu_permissions` table keys rows by these strings; AppShell's
 * `NAV_ITEMS` (Phase 2) carries one per entry. Order here is the source
 * of truth for sidebar order — keep it aligned with the visual nav.
 *
 * `chatbot` is intentionally absent: the legacy route still exists at
 * `/chatbot` but is hidden from the nav (see comment in AppShell.tsx).
 * The DB row for it is harmless — the hook ignores unknown menu_keys.
 */
export const MENU_KEYS = [
  'home',
  'contactos',
  'ventas',
  'compras',
  'importaciones',
  'recepcion',
  'inventario',
  'mantenimiento',
  'administracion',
  'configuracion',
  'aprobaciones',
  'reportes',
  'tesoreria',
  'dashboard',
] as const;

export type MenuKey = (typeof MENU_KEYS)[number];

/**
 * Cold-start / fetch-failure snapshot of who can see each module.
 * Byte-equivalent to the role tuples in `router.tsx` and `AppShell.tsx`
 * on the day this lands — the data layer is a refactor, not a behavior
 * change. If the `/api/auth/menu-permissions` fetch fails or hasn't
 * resolved yet, this is what `useMenuPermissions` returns so the
 * sidebar still renders something sensible (never empty, never locked
 * out).
 *
 * Also serves as the **defense-in-depth upper bound**: even after Phase
 * 3 lands and `ProtectedRoute` consults the DB, effective access stays
 * gated by both the DB row AND the hardcoded `allowedRoles`. Admins
 * can't grant a role access to a module just by flipping a Supabase
 * row beyond what the code already permits.
 *
 * Visitante is intentionally omitted — `canSeeMenu` short-circuits to
 * true for visitante (mirrors `hasRole`).
 */
export const MENU_PERMISSIONS_FALLBACK: Record<MenuKey, ReadonlyArray<UserRole>> = {
  home: ['admin', 'administracion', 'ventas', 'marketing'],
  contactos: ['admin', 'administracion', 'ventas', 'marketing'],
  ventas: ['admin', 'administracion', 'ventas', 'nova', 'marketing'],
  compras: ['admin', 'administracion'],
  importaciones: ['admin', 'administracion'],
  recepcion: ['admin', 'administracion', 'mantenimiento'],
  inventario: ['admin', 'administracion', 'ventas', 'mantenimiento', 'marketing'],
  mantenimiento: ['admin', 'administracion', 'mantenimiento'],
  administracion: ['admin', 'administracion', 'nova'],
  configuracion: ['admin', 'administracion'],
  aprobaciones: ['admin'],
  reportes: ['admin', 'administracion', 'nova'],
  tesoreria: ['admin', 'administracion', 'nova'],
  dashboard: ['admin'],
};

export function isMenuKey(value: string): value is MenuKey {
  return (MENU_KEYS as ReadonlyArray<string>).includes(value);
}
